import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Volume2, BookOpen, ChevronDown, RotateCcw } from 'lucide-react';
import { AudioTrack } from '../../data/audios';
interface AudioCardProps {
  audio: AudioTrack;
  index: number;
  isPlaying: boolean;
  progressPercent: number;
  currentTimeStr: string;
  registerRef: (id: string, el: HTMLAudioElement | null) => void;
  onTogglePlay: (id: string) => void;
  onRestart: (id: string) => void;
  onTimeUpdate: (id: string) => void;
  onEnded: (id: string) => void;
}
export function AudioCard({
  audio,
  index,
  isPlaying,
  progressPercent,
  currentTimeStr,
  registerRef,
  onTogglePlay,
  onRestart,
  onTimeUpdate,
  onEnded
}: AudioCardProps) {
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.2 + index * 0.1
  }} className={`bg-[#0c0c0e] border rounded-2xl p-5 sm:p-7 transition-all duration-300 ${isPlaying ? 'border-gold/40 shadow-[0_0_30px_rgba(212,175,55,0.15)]' : 'border-white/10 hover:border-white/20'}`} data-id="element-731">
      <audio ref={el => registerRef(audio.id, el)} src={audio.src} onTimeUpdate={() => onTimeUpdate(audio.id)} onEnded={() => onEnded(audio.id)} preload="metadata" data-id="element-732" />

      <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-start sm:items-center" data-id="element-733">
        {/* Play & Restart Buttons */}
        <div className="flex items-center gap-2 sm:flex-col sm:gap-2" data-id="element-734">
          <button onClick={() => onTogglePlay(audio.id)} className={`shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all duration-300 ${isPlaying ? 'bg-gold text-dark shadow-[0_0_20px_rgba(212,175,55,0.4)] scale-105' : 'bg-white/5 text-gold border border-gold/30 hover:bg-gold/10 hover:scale-105'}`} data-id="element-735">
            {isPlaying ? <Pause className="w-6 h-6 sm:w-7 sm:h-7 fill-current" data-id="element-736" /> : <Play className="w-6 h-6 sm:w-7 sm:h-7 fill-current ml-1" data-id="element-737" />}
          </button>
          <button onClick={() => onRestart(audio.id)} title="Volver a empezar" className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-white/5 text-white/40 border border-white/10 hover:text-gold hover:border-gold/30 transition-all duration-300" data-id="element-738">
            <RotateCcw className="w-4 h-4" data-id="element-739" />
          </button>
        </div>

        {/* Info & Progress */}
        <div className="flex-1 w-full" data-id="element-740">
          <div className="flex items-start justify-between gap-4 mb-2" data-id="element-741">
            <div data-id="element-742">
              <h3 className={`font-bold text-lg sm:text-xl mb-1 transition-colors ${isPlaying ? 'text-gold' : 'text-white'}`} data-id="element-743">
                {audio.title}
              </h3>
              <p className="text-white/50 text-xs sm:text-sm leading-relaxed" data-id="element-744">
                {audio.desc}
              </p>
            </div>
            {isPlaying && <Volume2 className="w-5 h-5 text-gold animate-pulse shrink-0 hidden sm:block" data-id="element-745" />}
          </div>

          {/* Progress Bar */}
          <div className="mt-4 sm:mt-5" data-id="element-746">
            <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden relative" data-id="element-747">
              <div className="absolute top-0 left-0 h-full bg-gradient-to-r from-gold/80 to-gold rounded-full transition-all duration-150 ease-linear" style={{
              width: `${progressPercent}%`
            }} data-id="element-748" />
            </div>
            <div className="flex justify-between items-center mt-2" data-id="element-749">
              <span className="text-[10px] sm:text-xs font-medium text-white/40 font-mono" data-id="element-750">
                {currentTimeStr}
              </span>
              <span className="text-[10px] sm:text-xs font-medium text-white/40 font-mono" data-id="element-751">
                {audio.duration}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Guide Toggle */}
      <button onClick={() => setIsGuideOpen(prev => !prev)} className="flex items-center gap-2 mt-4 text-gold/80 hover:text-gold transition-colors text-xs sm:text-sm font-bold group" data-id="element-752">
        <BookOpen className="w-4 h-4" data-id="element-753" />
        <span data-id="element-754">Cómo y cuándo escucharlo</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isGuideOpen ? 'rotate-180' : ''}`} data-id="element-755" />
      </button>

      {/* Guide Content */}
      <AnimatePresence data-id="element-756">
        {isGuideOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-757">
            <div className="mt-4 bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5 space-y-3" data-id="element-758">
              <div data-id="element-759">
                <p className="text-gold text-[10px] sm:text-xs font-black uppercase tracking-wider mb-1" data-id="element-760">
                  🕐 Cuándo
                </p>
                <p className="text-white/70 text-xs sm:text-sm leading-relaxed" data-id="element-761">
                  {audio.guide.when}
                </p>
              </div>
              <div className="border-t border-white/5 pt-3" data-id="element-762">
                <p className="text-gold text-[10px] sm:text-xs font-black uppercase tracking-wider mb-1" data-id="element-763">
                  🎧 Cómo
                </p>
                <p className="text-white/70 text-xs sm:text-sm leading-relaxed" data-id="element-764">
                  {audio.guide.how}
                </p>
              </div>
              <div className="border-t border-white/5 pt-3" data-id="element-765">
                <p className="text-gold text-[10px] sm:text-xs font-black uppercase tracking-wider mb-1" data-id="element-766">
                  ✨ Intención
                </p>
                <p className="text-white/70 text-xs sm:text-sm leading-relaxed" data-id="element-767">
                  {audio.guide.intention}
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}